import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { apiService } from "@/App";
import Header from "@/components/Header";
import { 
  Trophy, 
  TrendingUp,
  TrendingDown,
  Medal,
  Crown,
  Activity,
  Eye,
  ExternalLink,
  RefreshCw,
  Wallet,
  BarChart3
} from "lucide-react";

export default function WhaleLeaderboard() {
  const navigate = useNavigate();
  const [whales, setWhales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total_whales: 0,
    total_volume_usd: 0,
    avg_win_rate: 0
  });

  const fetchWhaleLeaderboard = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true);
      else setLoading(true);
      
      const res = await apiService.get("/whale-leaderboard");
      if (res.data.success) {
        setWhales(res.data.whales || []);
        setStats(res.data.stats || {
          total_whales: res.data.whales?.length || 0,
          total_volume_usd: 0,
          avg_win_rate: 0
        });
      }
    } catch (err) {
      console.error("Failed to fetch whale leaderboard:", err);
      // Show mock data for now
      setWhales(generateMockWhales());
      setStats({
        total_whales: 14247,
        total_volume_usd: 847500000,
        avg_win_rate: 54.3
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchWhaleLeaderboard();
  }, [fetchWhaleLeaderboard]);

  const generateMockWhales = () => {
    // Generate realistic mock whale data
    const whaleAddresses = [
      "0x8dxd...4f2a", "0xfrei...89bc", "0x7a3c...de01", "0xbeta...5678",
      "0x93fc...ab12", "0xalpha...cd34", "0x1234...ef56", "0xdefi...7890",
      "0xwhale...abcd", "0x9999...1234", "0xpro...5678", "0xquant...9abc"
    ];
    
    return whaleAddresses.map((addr, i) => ({
      rank: i + 1,
      address: addr,
      full_address: `0x${Math.random().toString(16).slice(2, 42)}`,
      total_volume_usd: Math.floor(Math.random() * 5000000) + 500000,
      total_pnl_usd: Math.floor(Math.random() * 1000000) - 200000,
      win_rate: Math.floor(Math.random() * 30) + 50,
      total_trades: Math.floor(Math.random() * 500) + 100,
      active_positions: Math.floor(Math.random() * 10),
      last_activity: new Date(Date.now() - Math.random() * 86400000).toISOString(),
      is_verified: Math.random() > 0.7,
      top_market: ["2024 Election", "BTC $100K", "ETH Merge", "Fed Rate"][Math.floor(Math.random() * 4)]
    }));
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="font-mono text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-gray-500/20 to-transparent border-gray-500/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-transparent border-amber-600/30';
    return 'bg-muted/30';
  };

  const formatUSD = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  const formatAddress = (addr) => {
    if (!addr) return "Unknown";
    if (addr.length <= 15) return addr;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <div className="hero-gradient py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Eye className="w-12 h-12 text-purple-400" />
            <Activity className="w-8 h-8 text-cyan-400 animate-pulse" />
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-wider mb-2">WHALE LEADERBOARD</h1>
          <p className="text-muted-foreground font-tech">Top whale wallets tracked by AI Poly-Copy & Hyper-Copy Bots</p>
          <div className="flex justify-center gap-2 mt-2">
            <Badge variant="outline" className="text-purple-400 border-purple-400">Polymarket</Badge>
            <Badge variant="outline" className="text-cyan-400 border-cyan-400">Hyperliquid</Badge>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mt-8">
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-purple-400">{stats.total_whales.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Whales Tracked</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-green-400">{formatUSD(stats.total_volume_usd)}</p>
              <p className="text-xs text-muted-foreground">Total Volume</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-mono font-bold text-primary">{stats.avg_win_rate}%</p>
              <p className="text-xs text-muted-foreground">Avg Win Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-6">
        {/* Refresh Button */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-sm text-muted-foreground">
            Top whales from Polymarket & Hyperliquid
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchWhaleLeaderboard(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Top 3 Podium */}
        {whales.length >= 3 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            {/* 2nd Place */}
            <Card className={`glass-card ${getRankBg(2)} mt-8`}>
              <CardContent className="p-4 text-center">
                <Medal className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gray-500/20 flex items-center justify-center">
                  <Wallet className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-mono text-sm truncate">{formatAddress(whales[1]?.address)}</h3>
                <p className={`font-mono font-bold mt-2 ${whales[1]?.total_pnl_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {whales[1]?.total_pnl_usd >= 0 ? '+' : ''}{formatUSD(whales[1]?.total_pnl_usd || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {whales[1]?.win_rate}% Win Rate
                </p>
              </CardContent>
            </Card>

            {/* 1st Place */}
            <Card className={`glass-card ${getRankBg(1)} border-2`}>
              <CardContent className="p-4 text-center">
                <Crown className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                <div className="w-20 h-20 mx-auto mb-2 rounded-full bg-yellow-500/20 border-2 border-yellow-400 flex items-center justify-center">
                  <Wallet className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="font-mono text-sm truncate">{formatAddress(whales[0]?.address)}</h3>
                <p className={`font-mono font-bold text-xl mt-2 ${whales[0]?.total_pnl_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {whales[0]?.total_pnl_usd >= 0 ? '+' : ''}{formatUSD(whales[0]?.total_pnl_usd || 0)}
                </p>
                <Badge className="mt-2 bg-yellow-500/20 text-yellow-400">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {whales[0]?.win_rate}% Win Rate
                </Badge>
              </CardContent>
            </Card>

            {/* 3rd Place */}
            <Card className={`glass-card ${getRankBg(3)} mt-8`}>
              <CardContent className="p-4 text-center">
                <Medal className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-amber-600/20 flex items-center justify-center">
                  <Wallet className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-mono text-sm truncate">{formatAddress(whales[2]?.address)}</h3>
                <p className={`font-mono font-bold mt-2 ${whales[2]?.total_pnl_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {whales[2]?.total_pnl_usd >= 0 ? '+' : ''}{formatUSD(whales[2]?.total_pnl_usd || 0)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {whales[2]?.win_rate}% Win Rate
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Full Leaderboard */}
        <Card className="glass-card">
          <CardHeader className="border-b border-border flex flex-row items-center justify-between">
            <CardTitle className="font-heading tracking-wider flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-400" />
              WHALE RANKINGS
            </CardTitle>
            <Badge variant="outline" className="text-purple-400 border-purple-400">
              {whales.length} Whales
            </Badge>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto mb-2 animate-pulse text-purple-400" />
                <p>Scanning whale wallets...</p>
              </div>
            ) : whales.length === 0 ? (
              <div className="p-12 text-center">
                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No whale data available yet.</p>
                <p className="text-sm text-muted-foreground mt-2">Start a Poly-Copy Bot to track whales!</p>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="divide-y divide-border">
                  {whales.map((whale, index) => (
                    <div 
                      key={whale.address || index}
                      className={`p-3 sm:p-4 hover:bg-muted/30 transition-colors ${getRankBg(whale.rank)}`}
                      data-testid={`whale-entry-${index}`}
                    >
                      {/* Mobile: Stack vertically, Desktop: Row layout */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
                          <div className="w-8 sm:w-10 flex justify-center flex-shrink-0">
                            {getRankIcon(whale.rank)}
                          </div>
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            whale.source === 'hyperliquid' ? 'bg-cyan-500/20' : 'bg-purple-500/20'
                          }`}>
                            <Wallet className={`w-4 h-4 sm:w-5 sm:h-5 ${
                              whale.source === 'hyperliquid' ? 'text-cyan-400' : 'text-purple-400'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-mono text-xs sm:text-sm truncate">{formatAddress(whale.address)}</h3>
                              <Badge variant="outline" className={`text-[10px] px-1 py-0 ${
                                whale.source === 'hyperliquid' ? 'text-cyan-400 border-cyan-400' : 'text-purple-400 border-purple-400'
                              }`}>
                                {whale.source === 'hyperliquid' ? 'HL' : 'PM'}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">
                              {whale.total_trades} trades • {whale.top_market}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 sm:gap-6 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground hidden sm:block">Win</p>
                            <p className="font-mono font-bold text-xs sm:text-sm">{whale.win_rate}%</p>
                          </div>
                          <div className="text-right min-w-[60px] sm:min-w-[80px]">
                            <p className="text-xs text-muted-foreground hidden sm:block">P&L</p>
                            <p className={`font-mono font-bold text-xs sm:text-sm ${whale.total_pnl_usd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {whale.total_pnl_usd >= 0 ? '+' : ''}{formatUSD(whale.total_pnl_usd)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="mt-6 bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Activity className="w-5 h-5 text-purple-400 mt-0.5" />
              <div>
                <p className="font-medium text-purple-400">About Whale Tracking</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Our AI Poly-Copy Bot scans 14,000+ Polymarket whale wallets in real-time. 
                  When 3+ whales enter the same position, your bot can auto-copy their trades 
                  using real STXOSHI funds. Create a Poly-Copy Bot to start following the smart money!
                </p>
                <Button 
                  className="mt-3 bg-purple-500 hover:bg-purple-600"
                  size="sm"
                  onClick={() => navigate('/create?type=bot')}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Create Poly-Copy Bot
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
